import { useEffect, useState } from "react";
import { Building2, Plus, Pencil, Power } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, apiErr } from "@/lib/api";

const EMPTY = { name: "", internal_code: "", address: "", city: "", region: "", country: "Chile", timezone: "America/Santiago", phone: "", notes: "" };

export default function Locales() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/locations?include_inactive=true").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (l) => { setForm({ ...EMPTY, ...l }); setEditId(l.id); setOpen(true); };

  const save = async () => {
    if (!form.name) { toast.error("El nombre es obligatorio"); return; }
    const payload = { name: form.name, internal_code: form.internal_code, address: form.address, city: form.city, region: form.region, country: form.country, timezone: form.timezone, phone: form.phone, notes: form.notes };
    try {
      if (editId) await api.put(`/locations/${editId}`, payload);
      else await api.post("/locations", payload);
      toast.success("Local guardado"); setOpen(false); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const toggle = async (l) => {
    if (l.active) { await api.post(`/locations/${l.id}/deactivate`); toast.success("Local desactivado"); }
    else { await api.put(`/locations/${l.id}`, { ...l }); }
    load();
  };

  return (
    <div>
      <PageHeader title="Locales" subtitle="Administra los locales de tu empresa"
        right={<Button data-testid="new-location-btn" onClick={openNew} className="rounded-xl"><Plus className="mr-2 h-4 w-4" /> Nuevo local</Button>} />
      {items.length === 0 ? (
        <EmptyState icon={Building2} title="Sin locales" subtitle="Crea tu primer local." action={<Button onClick={openNew} className="rounded-xl"><Plus className="mr-2 h-4 w-4" /> Nuevo local</Button>} />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((l) => (
            <div key={l.id} data-testid={`location-${l.id}`} className={`rounded-2xl border bg-white p-4 ${l.active ? "border-slate-200" : "border-slate-200 opacity-60"}`}>
              <div className="flex items-start justify-between">
                <div><p className="font-medium">{l.name}</p><p className="text-xs text-slate-500">{l.internal_code} · {l.city}</p></div>
                {!l.active && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">Inactivo</span>}
              </div>
              <p className="mt-2 text-sm text-slate-500">{l.address}</p>
              <div className="mt-4 flex gap-2">
                <Button data-testid={`edit-location-${l.id}`} size="sm" variant="outline" onClick={() => openEdit(l)} className="rounded-lg"><Pencil className="mr-1 h-3.5 w-3.5" /> Editar</Button>
                <Button size="sm" variant="ghost" onClick={() => toggle(l)} className="rounded-lg text-slate-500"><Power className="mr-1 h-3.5 w-3.5" /> {l.active ? "Desactivar" : "Activar"}</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-display">{editId ? "Editar local" : "Nuevo local"}</DialogTitle></DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Nombre *" testid="loc-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
            <Field label="Código interno" testid="loc-code" value={form.internal_code} onChange={(v) => setForm({ ...form, internal_code: v })} />
            <Field label="Dirección" testid="loc-address" value={form.address} onChange={(v) => setForm({ ...form, address: v })} />
            <Field label="Ciudad" testid="loc-city" value={form.city} onChange={(v) => setForm({ ...form, city: v })} />
            <Field label="Región" value={form.region} onChange={(v) => setForm({ ...form, region: v })} />
            <Field label="País" value={form.country} onChange={(v) => setForm({ ...form, country: v })} />
            <Field label="Zona horaria" value={form.timezone} onChange={(v) => setForm({ ...form, timezone: v })} />
            <Field label="Teléfono" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
          </div>
          <DialogFooter><Button data-testid="save-location-btn" onClick={save} className="rounded-xl">Guardar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, value, onChange, testid }) {
  return <div><Label className="text-sm">{label}</Label><Input data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1 h-11" /></div>;
}
